import { useSelector } from "react-redux";
import { ObjectTarget, } from "../../../app/permissions";
import { assertThumbnailUrl, getBackendUrl, LoadState } from "../../../redux/common";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { stringAvatar, stringToColor } from "../../user/avatarUtils";
import { ConversationInfo, ConversationMessage, datetimeSimplifier, timeSimplifier } from "../../../redux/conversation/conversationSlice";
import { ObjectRetrieveRequest, retrieveObject, selectObjectCache } from "../../../redux/object/objectCacheSlice";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { NamedObject } from "../../../redux/object/namedObject";
import { getMessagePriorityColor, getMessagePriorityName, MessagePriority } from "../../../redux/conversation/messagePriority";
import { MessageContent } from "./MessageContent";

interface MessageTileOtherProps {
    conversation: ConversationInfo;
    message: ConversationMessage;
    content?: React.ReactNode;
}

export const MessageTileOther = (props: MessageTileOtherProps) => {
    const msg = props.message;
    const dt = timeSimplifier(msg.processed);
    const edited = msg.edited ? `Editado ${datetimeSimplifier(msg.edited)}` : "";
    const dispatch = useAppDispatch();

    const message = <MessageContent message={msg} content={props.content} align="left" />;

    const [user, setUser] = useState(undefined as undefined | NamedObject);
    const [editUser, setEditUser] = useState(undefined as undefined | NamedObject);
    const objectCache = useSelector(selectObjectCache);

    const reqUser = useMemo((): ObjectRetrieveRequest => {
        return {
            objectTarget: ObjectTarget.USER,
            targetId: `${msg.userId}`,
        };
    }, [msg.userId]);
    const reqEditUser = useMemo((): ObjectRetrieveRequest => {
        return {
            objectTarget: ObjectTarget.USER,
            targetId: `${msg.editUserId}`,
        };
    }, [msg.editUserId]);

    useEffect(() => {
        if (!msg.userId) {
            return;
        }
        const foundObject = (objectCache.cache.users[reqUser.targetId]);
        if (foundObject) {
            setUser(NamedObject.user(foundObject));
        }
        if (!foundObject) {
            let state = LoadState.NotStarted;
            if (objectCache.requests[reqUser.objectTarget + ":" + reqUser.targetId]
                && objectCache.requests[reqUser.objectTarget + ":" + reqUser.targetId].state) {
                state = objectCache.requests[reqUser.objectTarget + ":" + reqUser.targetId].state;
            }
            if (state == LoadState.NotStarted) {
                dispatch(retrieveObject(reqUser));
            }
        }
    }, [reqUser, objectCache]);

    useEffect(() => {
        if (!msg.editUserId) {
            return;
        }
        const foundObject = (objectCache.cache.users[reqEditUser.targetId]);
        if (foundObject) {
            setEditUser(NamedObject.user(foundObject));
        }
        if (!foundObject) {
            let state = LoadState.NotStarted;
            if (objectCache.requests[reqEditUser.objectTarget + ":" + reqEditUser.targetId]
                && objectCache.requests[reqEditUser.objectTarget + ":" + reqEditUser.targetId].state) {
                state = objectCache.requests[reqEditUser.objectTarget + ":" + reqEditUser.targetId].state;
            }
            if (state == LoadState.NotStarted) {
                dispatch(retrieveObject(reqEditUser));
            }
        }
    }, [reqEditUser, objectCache]);

    let image = undefined;
    let name = msg.partyId || "Unknown User";
    let titleColor = stringToColor(name);
    if (user) {
        image = assertThumbnailUrl(user.url);
        name = user.display;
        titleColor = stringToColor(name);
    } else {
        name = "Sistema";
        image = getBackendUrl() + "/api/image/priza.png";
        titleColor = "purple";
    }
    let editImage = undefined;
    let editName = "-";
    if (editUser) {
        editImage = assertThumbnailUrl(editUser.url);
        editName = editUser.display;
    } else if (msg.editUserId) {
        editName = `${msg.editUserId}`;
        editImage = "";
    }

    return <Box sx={{ marginRight: 16, marginY: 1 }}>
        <Stack direction="row" spacing={1}>
            <Box sx={{ width: 32, height: 32, alignContent: "center", justifyContent: "center", justifyItems: "center" }}>
                <Avatar sx={{ width: 32, height: 32, m: 0, border: "1px solid gray", backgroundColor: stringToColor(name) }}
                    variant='circular' alt={`${name}`} src={`${image}`}>
                    <Typography variant="h6" component="span" fontSize={13}>
                        {stringAvatar(name)}
                    </Typography>
                </Avatar>
            </Box>
            <Stack direction="column" spacing={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" component="span" fontSize={11} color={titleColor} sx={{ marginY: "-2px" }}>
                        {name}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="caption" component="span" fontSize={9} sx={{ textAlign: "right", color: "gray", marginY: "-2px" }}>
                        {dt}
                    </Typography>
                </Stack>
                <Box key={dt}
                    sx={{
                        padding: 1,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: (theme) =>
                            msg.messagePriority == MessagePriority.Regular ? "black" :
                                getMessagePriorityColor(theme, msg.messagePriority).contrastText,
                        backgroundColor: (theme) =>
                            msg.messagePriority == MessagePriority.Regular ? "lightgray" :
                                getMessagePriorityColor(theme, msg.messagePriority).main,
                        minWidth: 300
                    }}>
                    {
                        msg.messagePriority != MessagePriority.Regular &&
                        <Stack direction="row" alignItems="center">
                            <Box sx={{ flexGrow: 1 }} />
                            <Typography variant="caption" component="div" sx={{
                                color: (theme) => getMessagePriorityColor(theme, msg.messagePriority).contrastText,
                                fontWeight: "bold",
                            }}>
                                {`[${getMessagePriorityName(msg.messagePriority)}]`}
                            </Typography>
                        </Stack>
                    }
                    <Typography variant="body2" component="span" sx={{
                        color: (theme) =>
                            msg.messagePriority == MessagePriority.Regular ? "black" :
                                getMessagePriorityColor(theme, msg.messagePriority).contrastText,
                        overflowWrap: "anywhere"
                    }}>
                        {message}
                    </Typography>
                </Box>
                {edited &&
                    <Stack direction="column" alignItems="left" sx={{ marginLeft: 1, marginTop: "0px", marginBottom: "3px" }}>
                        <Typography variant="caption" component="span" fontSize={10} sx={{ textAlign: "left", color: "#555", marginY: "-2px" }}>
                            {edited}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 16, height: 16, m: 0, border: "1px solid gray", backgroundColor: stringToColor(editName) }}
                                variant='circular' alt={`${editName}`} src={`${editImage}`}>
                                <Typography variant="h6" component="span" fontSize={9}>
                                    {stringAvatar(editName)}
                                </Typography>
                            </Avatar>
                            <Typography variant="caption" component="span" fontSize={10} sx={{ textAlign: "left", color: "#555"}}>
                                {editName}
                            </Typography>
                        </Stack>
                    </Stack>
                }
            </Stack>
        </Stack>
    </Box>;
};